import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getCookie } from '../../http/cookies';
import {
  baseUrl,
  fimboLessonLinkAddUrlPref,
  fimboLessonLinkChangeUrlPref,
  genreLessonLinkAddUrlPref,
  genreLessonLinkChangeUrlPref,
  lessonUrlPref,
} from '../../http/urls';
import { FimboLessonLink, GenreLessonLink, Lesson } from '../../models/Models';
import { ILessonOneResponse, ILessonResponse } from '../../models/StoreModels';

const lessonListTag = { type: 'Lesson' as const, id: 'LIST' };

export const LessonApi = createApi({
  reducerPath: 'lesson',
  baseQuery: fetchBaseQuery({
    baseUrl: baseUrl,
  }),
  tagTypes: ['Lesson'],
  endpoints: (build) => ({
    fetchAllLessons: build.query<ILessonResponse, string>({
      query: (params) => ({
        url: `${lessonUrlPref}${params && '?' + params}`,
        headers: {
          Authorization: `Bearer ${getCookie('token')}`,
        },
      }),
      providesTags: (result) =>
        result?.rows?.length
          ? [...result.rows.map((row) => ({ type: 'Lesson' as const, id: String(row.id) })), lessonListTag]
          : [lessonListTag],
    }),
    fetchLesson: build.query<ILessonOneResponse, string>({
      query: (id) => ({
        url: `${lessonUrlPref}/${id}`,
        headers: {
          Authorization: `Bearer ${getCookie('token')}`,
        },
      }),
      providesTags: (result, error, id) => [{ type: 'Lesson', id }],
    }),
    createLesson: build.mutation<Lesson, Lesson>({
      query: (post) => ({
        url: lessonUrlPref,
        method: 'POST',
        body: post,
        headers: {
          Authorization: `Bearer ${getCookie('token')}`,
        },
      }),
      invalidatesTags: [lessonListTag],
    }),

    changeLesson: build.mutation<Lesson, Lesson>({
      query: (post) => ({
        url: lessonUrlPref,
        method: 'PUT',
        body: post,
        headers: {
          Authorization: `Bearer ${getCookie('token')}`,
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Lesson', id: String(arg.id) },
        lessonListTag,
      ],
    }),

    deleteLesson: build.mutation<Lesson, string>({
      query: (id) => ({
        url: lessonUrlPref,
        method: 'DELETE',
        body: { id },
        headers: {
          Authorization: `Bearer ${getCookie('token')}`,
        },
      }),
      /** Список + деталь по id: лишний GET детали не уходит, если подписка уже снята (см. navigate до ответа в DeleteLesson). */
      invalidatesTags: (result, error, id) => [lessonListTag, { type: 'Lesson', id }],
    }),

    addLessonLinks: build.mutation<FimboLessonLink[], { fimbos: number[]; lesson_id: number }>({
      query: (post) => ({
        url: fimboLessonLinkAddUrlPref,
        method: 'POST',
        body: post,
        headers: {
          Authorization: `Bearer ${getCookie('token')}`,
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Lesson', id: String(arg.lesson_id) },
        lessonListTag,
      ],
    }),

    changeLessonLinks: build.mutation<FimboLessonLink[], { fimbos: number[]; lesson_id: number }>({
      query: (post) => ({
        url: fimboLessonLinkChangeUrlPref,
        method: 'PUT',
        body: post,
        headers: {
          Authorization: `Bearer ${getCookie('token')}`,
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Lesson', id: String(arg.lesson_id) },
        lessonListTag,
      ],
    }),

    addGenreLessonLinks: build.mutation<GenreLessonLink[], { genres: number[]; lesson_id: number }>({
      query: (post) => ({
        url: genreLessonLinkAddUrlPref,
        method: 'POST',
        body: post,
        headers: {
          Authorization: `Bearer ${getCookie('token')}`,
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Lesson', id: String(arg.lesson_id) },
        lessonListTag,
      ],
    }),

    changeGenreLessonLinks: build.mutation<GenreLessonLink[], { genres: number[]; lesson_id: number }>({
      query: (post) => ({
        url: genreLessonLinkChangeUrlPref,
        method: 'PUT',
        body: post,
        headers: {
          Authorization: `Bearer ${getCookie('token')}`,
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Lesson', id: String(arg.lesson_id) },
        lessonListTag,
      ],
    }),

    bulkUpdateLessons: build.mutation<{ success: boolean; message: string; updatedCount: number }, { lessonIds: number[]; action: 'hide' | 'show' | 'delete' }>({
      query: (post) => ({
        url: `${lessonUrlPref}/bulk`,
        method: 'PUT',
        body: post,
        headers: {
          Authorization: `Bearer ${getCookie('token')}`,
        },
      }),
      invalidatesTags: [lessonListTag],
    }),
  }),
});
